import { apiClient } from './apiClient';

const loyaltySummaryCache = new Map<string, { summary: LoyaltySummary | null; timestamp: number }>();
const inflightSummaryPromises = new Map<string, Promise<LoyaltySummary | null>>();
const SUMMARY_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export interface LoyaltySummary {
  memberId: string;
  tier: string;
  totalPoints: number;
  joinedDate?: string;
  isPaid?: boolean;
  nextTier?: string;
  pointsNeeded?: number;
}

export interface GiftPointsPayload {
  points: number;
  description: string;
  sendNotification?: boolean;
  postToFeed?: boolean;
}

export interface GiftPointsResult {
  success: boolean;
  transactionId: string;
  points: number;
  memberId: string;
  memberName: string;
  feedPostId?: string;
  notificationSent: boolean;
}

export interface LoyaltyTransaction {
  id: string;
  memberId: string;
  points: number;
  transactionType: string;
  notes: string;
  timestamp: string;
}

export interface PointsPackage {
  id: string;
  name: string;
  rsvpPoints: number;
  checkInPoints: number;
  bostonBonusPoints: number;
  isDefault: boolean;
}

export interface PointsTier {
  name: string;
  pointsRequired: number;
  keepStatus: boolean;
}

export interface MembershipTimeframe {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  price: number;
  points: number;
  description: string;
  pointsTiers?: PointsTier[];
}

export interface AuditLogItem {
  id: string;
  timestamp: string;
  action: string;
  actorId: string;
  actorName: string;
  targetId: string;
  notes: string;
}

export const loyaltyApi = {
  // Point Packages CRUD
  getPointsPackages: async (): Promise<PointsPackage[]> => {
    try {
      return await apiClient.get<PointsPackage[]>('/pointspackages');
    } catch (e) {
      return [];
    }
  },

  getPointsPackageById: async (id: string): Promise<PointsPackage | null> => {
    try {
      return await apiClient.get<PointsPackage>(`/pointspackages/${id}`);
    } catch (e) {
      return null;
    }
  },

  createPointsPackage: async (pkg: PointsPackage): Promise<boolean> => {
    try {
      await apiClient.post('/pointspackages', pkg);
      return true;
    } catch (e) {
      return false;
    }
  },

  updatePointsPackage: async (pkg: PointsPackage): Promise<boolean> => {
    try {
      await apiClient.put(`/pointspackages/${pkg.id}`, pkg);
      return true;
    } catch (e) {
      return false;
    }
  },

  deletePointsPackage: async (id: string): Promise<boolean> => {
    try {
      await apiClient.delete(`/pointspackages/${id}`);
      return true;
    } catch (e) {
      return false;
    }
  },

  // Loyalty Ledger
  getLoyaltySummary: async (memberId: string, forceRefresh = false): Promise<LoyaltySummary | null> => {
    if (!memberId) return null;
    const now = Date.now();
    const cached = loyaltySummaryCache.get(memberId);
    if (!forceRefresh && cached && now - cached.timestamp < SUMMARY_CACHE_TTL_MS) {
      return cached.summary;
    }

    const inflight = inflightSummaryPromises.get(memberId);
    if (inflight) {
      return inflight;
    }

    const promise = (async () => {
      try {
        const res = await apiClient.get<LoyaltySummary>(`/loyalty/summary/${memberId}`);
        loyaltySummaryCache.set(memberId, { summary: res, timestamp: Date.now() });
        return res;
      } catch (e) {
        return cached?.summary || null;
      } finally {
        inflightSummaryPromises.delete(memberId);
      }
    })();

    inflightSummaryPromises.set(memberId, promise);
    return promise;
  },

  invalidateLoyaltySummaryCache: (memberId?: string) => {
    if (memberId) {
      loyaltySummaryCache.delete(memberId);
      inflightSummaryPromises.delete(memberId);
    } else {
      loyaltySummaryCache.clear();
      inflightSummaryPromises.clear();
    }
  },

  getLoyaltyTransactions: async (memberId: string): Promise<LoyaltyTransaction[]> => {
    try {
      return await apiClient.get<LoyaltyTransaction[]>(`/loyalty/ledger/${memberId}`);
    } catch (e) {
      return [];
    }
  },

  getEventTransactions: async (eventId: string): Promise<LoyaltyTransaction[]> => {
    try {
      return await apiClient.get<LoyaltyTransaction[]>(`/loyalty/event-transactions/${eventId}`);
    } catch (e) {
      return [];
    }
  },

  payDues: async (memberId: string): Promise<boolean> => {
    try {
      await apiClient.post(`/loyalty/dues/${memberId}`);
      return true;
    } catch (e) {
      return false;
    }
  },

  recordRsvp: async (eventId: string, memberId: string): Promise<boolean> => {
    try {
      await apiClient.post('/loyalty/rsvp', { eventId, memberId });
      return true;
    } catch (e) {
      return false;
    }
  },

  cancelRsvp: async (eventId: string, memberId: string): Promise<boolean> => {
    try {
      await apiClient.post('/loyalty/cancelrsvp', { eventId, memberId });
      return true;
    } catch (e) {
      return false;
    }
  },

  checkInMember: async (eventId: string, memberId: string): Promise<boolean> => {
    await apiClient.post('/loyalty/checkin', { eventId, memberId });
    return true;
  },

  recordNoShow: async (eventId: string, memberId: string): Promise<boolean> => {
    try {
      await apiClient.post('/loyalty/noshow', { eventId, memberId });
      return true;
    } catch (e) {
      return false;
    }
  },

  recordBostonBonus: async (eventId: string, memberId: string): Promise<boolean> => {
    try {
      await apiClient.post('/loyalty/boston', { eventId, memberId });
      return true;
    } catch (e) {
      return false;
    }
  },

  // Membership Timeframes CRUD
  getMembershipTimeframes: async (): Promise<MembershipTimeframe[]> => {
    try {
      return await apiClient.get<MembershipTimeframe[]>('/memberships');
    } catch (e) {
      return [];
    }
  },

  getMembershipTimeframeById: async (id: string): Promise<MembershipTimeframe | null> => {
    try {
      return await apiClient.get<MembershipTimeframe>(`/memberships/${id}`);
    } catch (e) {
      return null;
    }
  },

  createMembershipTimeframe: async (doc: MembershipTimeframe): Promise<boolean> => {
    await apiClient.post('/memberships', doc);
    return true;
  },

  updateMembershipTimeframe: async (doc: MembershipTimeframe): Promise<boolean> => {
    await apiClient.put(`/memberships/${doc.id}`, doc);
    return true;
  },

  deleteMembershipTimeframe: async (id: string): Promise<boolean> => {
    await apiClient.delete(`/memberships/${id}`);
    return true;
  },

  // Detailed Member Admin & Points CRUD
  getAdminMemberDetails: async (memberId: string): Promise<any | null> => {
    try {
      return await apiClient.get<any>(`/admin/members/${memberId}`);
    } catch (e) {
      return null;
    }
  },

  updateAdminMemberProfile: async (memberId: string, profile: any): Promise<boolean> => {
    // Helper to robustly parse expiration date formats
    const parseExpirationDate = (val: any): string | null => {
      if (!val) return null;
      if (typeof val.toDate === 'function') return val.toDate().toISOString();
      if (typeof val === 'object' && typeof val.seconds === 'number') return new Date(val.seconds * 1000).toISOString();
      if (val instanceof Date) return val.toISOString();
      if (typeof val === 'string') {
        const trimmed = val.trim();
        if (trimmed === '') return null;
        const d = new Date(trimmed);
        if (!isNaN(d.getTime())) return d.toISOString();
        return null;
      }
      return null;
    };

    const cleanedProfile = { 
      ...profile,
      membershipExpirationDate: parseExpirationDate(profile.membershipExpirationDate)
    };

    try {
      await apiClient.put(`/admin/members/${memberId}`, cleanedProfile);
      return true;
    } catch (e) {
      return false;
    }
  },

  getMemberMembershipHistory: async (memberId: string): Promise<any[]> => {
    try {
      return await apiClient.get<any[]>(`/admin/members/${memberId}/membership-history`);
    } catch (e) {
      return [];
    }
  },

  assignMemberMembership: async (memberId: string, timeframeId: string): Promise<boolean> => {
    try {
      await apiClient.post(`/admin/members/${memberId}/assign-membership`, { membershipTimeframeId: timeframeId });
      return true;
    } catch (e) {
      return false;
    }
  },

  deleteMemberMembership: async (memberId: string, membershipId: string): Promise<boolean> => {
    try {
      await apiClient.delete(`/admin/members/${memberId}/membership/${membershipId}`);
      return true;
    } catch (e) {
      return false;
    }
  },

  inactivateMemberMembership: async (memberId: string, membershipId: string): Promise<boolean> => {
    try {
      await apiClient.post(`/admin/members/${memberId}/membership/${membershipId}/inactivate`);
      return true;
    } catch (e) {
      return false;
    }
  },

  clearMemberMembership: async (memberId: string): Promise<boolean> => {
    try {
      await apiClient.post(`/admin/members/${memberId}/clear-membership`);
      return true;
    } catch (e) {
      return false;
    }
  },

  addPointsTransaction: async (memberId: string, tx: Omit<LoyaltyTransaction, 'id'>): Promise<boolean> => {
    try {
      await apiClient.post(`/admin/members/${memberId}/points`, tx);
      return true;
    } catch (e) {
      return false;
    }
  },

  updatePointsTransaction: async (memberId: string, transactionId: string, tx: LoyaltyTransaction): Promise<boolean> => {
    try {
      await apiClient.put(`/admin/members/${memberId}/points/${transactionId}`, tx);
      return true;
    } catch (e) {
      return false;
    }
  },

  deletePointsTransaction: async (memberId: string, transactionId: string): Promise<boolean> => {
    try {
      await apiClient.delete(`/admin/members/${memberId}/points/${transactionId}`);
      return true;
    } catch (e) {
      return false;
    }
  },

  giftPoints: async (memberId: string, payload: GiftPointsPayload): Promise<GiftPointsResult> => {
    return await apiClient.post<GiftPointsResult>(`/admin/members/${memberId}/gift-points`, payload);
  },

  // Audit Logs Admin
  getAuditLogs: async (): Promise<AuditLogItem[]> => {
    try {
      const rawLogs = await apiClient.get<any[]>('/admin/audit-logs');
      return rawLogs.map(log => ({
        id: log.id || log.Id || "",
        timestamp: log.timestamp || log.Timestamp || "",
        action: log.action || log.Action || "",
        actorId: log.userId || log.UserId || log.actorId || log.ActorId || "",
        actorName: log.userDisplayName || log.UserDisplayName || log.actorName || log.ActorName || log.userEmail || log.UserEmail || "System",
        targetId: log.targetId || log.TargetId || "",
        notes: log.details || log.Details || log.notes || log.Notes || ""
      }));
    } catch (e) {
      return [];
    }
  },

  // System Maintenance
  runSystemCleanup: async (options?: { dryRun?: boolean; telemetryRetentionDays?: number; auditLogRetentionDays?: number }): Promise<{
    success: boolean;
    tablesDeleted: number;
    telemetryLogsDeleted: number;
    auditLogsDeleted: number;
    errors: string[];
    durationMs: number;
    message: string;
  }> => {
    return await apiClient.post('/admin/maintenance/cleanup', options || {});
  },
};

